<template>
  <div
    ref="theNote"
    class="rounded-lg select-none flex cursor-grab border border-gray-800"
    :class="[getBackgorundHighlight(), { 'ring-4 ring-amber-400': isSelected }]"
    role="group"
    :aria-roledescription="t('contribution')"
    :aria-label="accessibleLabel"
  >
    <!-- Shape cue for the selection, so it does not rely on the ring colour alone (WCAG 1.4.1) -->
    <img
      v-if="isSelected"
      :src="CircleCheck"
      alt=""
      aria-hidden="true"
      class="absolute -top-3 -left-3 z-10 w-6 h-6 rounded-full bg-amber-400 pointer-events-none"
    />
    <Teleport to="body">
      <NoteMenu
        v-if="state.menuIsVisible"
        class="note-floating-menu"
        :note="props.note"
        :board-id="props.boardId"
        :wall-id="wallStore.getters.selectedWall.value?._id"
        :style="{
          position: 'fixed',
          bottom: menuPosition.bottom + 'px',
          right: menuPosition.right + 'px',
          zIndex: 1000,
        }"
        @delete-note="deleteNote()"
        @close-menu="closeMenu()"
        @show-image-popup="emit('showImagePopup', note)"
      />
    </Teleport>
    <div class="flex flex-col-reverse w-full relative px-1.5 py-1">
      <Notice v-if="state.invalidUrl" :type="DialogMessageType.Error" :text="t('scrapeUnsuccessful')" />

      <!-- Controls -->
      <div class="flex flex-row justify-start p-1 items-center flex-wrap">
        <div class="flex justify-between items-center text-sm" :class="noteMetaStyles">
          <div class="flex items-center whitespace-nowrap flex-column flex-wrap">
            <div class="flex flex-col items-start mr-6">
              <div class="rounded-lg" :style="`background-color: ${color.actions.selectColor(props.note.status.createdBy.username).background};`">
                <p class="text-left mx-1" :style="`color: ${color.actions.selectColor(props.note.status.createdBy.username).text};`">
                  {{ shortenedUserName(props.note.status.createdBy.username) }}
                </p>
              </div>
              <p v-if="!state.editHistoryIsVisible" class="flex items-center justify-center -mb-1 text-sm" :class="state.textClass">
                {{ common.actions.getCleanTimestamp(props.note.status.created) }}
              </p>
            </div>
            <div v-if="zoomLevel >= 1" class="flex relative mt-2">
              <div v-if="props.note.origins.length > 1" class="flex mr-2 w-5 h-5 justify-center items-center" :class="state.textClass">
                <img
                  :src="Pin"
                  class="w-4 h-4 opacity-70"
                  :alt="t('aria-status-origins')"
                  :class="{
                    invert: !props.note.entries[selectedBoardId].highlightColor.includes('bg-white'),
                  }"
                />
                <div class="-ml-1 -mt-5 bg-slate-middle rounded-full text-xs opacity-70">
                  <p class="w-4">{{ props.note.origins.length - 1 }}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Normal icons — hidden while editing -->
        <div v-if="zoomLevel >= 1 && !state.textEditIsActive" ref="noteOptionsWrapper" class="flex flex-row justify-start cursor-default flex-grow">
          <!-- History -->
          <div class="flex items-center justify-center text-sm mr-1">
            <div
              v-if="props.note.edits.length > 0"
              tabindex="0"
              class="w-8 h-8 flex justify-center bg-black/20 items-center cursor-pointer rounded-full"
              @pointerdown.stop="showEditHistory()"
            >
              <img
                :src="History"
                class="absolute w-3 h-3"
                :class="{
                  invert: !props.note.entries[selectedBoardId].highlightColor.includes('bg-white'),
                }"
                :alt="t('aria-status-history')"
              />
              <div class="ml-6 absolute opacity-70 -mt-5 bg-slate-middle rounded-full text-xs">
                <p class="text-white w-4 opacity-70">{{ props.note.edits.length }}</p>
              </div>
            </div>
          </div>

          <!-- Menu -->
          <div
            v-if="actionAllowed && zoomLevel >= 1"
            tabindex="0"
            ref="menuTriggerBtn"
            class="w-8 h-8 flex justify-center bg-black/20 items-center cursor-pointer rounded-full"
            @pointerdown.stop="toggleMenu()"
          >
            <img
              :src="Menu"
              class="w-4 h-4 invert"
              :class="{
                'opacity-70 invert-0': note.entries[selectedBoardId].highlightColor.includes('bg-white'),
              }"
              :alt="t('aria-note-menu')"
            />
          </div>

          <!-- Edit -->
          <div
            class="w-8 h-8 flex justify-center bg-black/20 items-center cursor-pointer rounded-full ml-auto"
            tabindex="0"
            @pointerdown.prevent.stop="toggleEditor()"
          >
            <img
              :src="getEditIconByPermission()"
              class="w-3 h-3 invert"
              :class="{
                'opacity-70 invert-0': note.entries[selectedBoardId].highlightColor.includes('bg-white'),
              }"
              :alt="t('aria-edit-note')"
            />
          </div>
        </div>

        <!-- Confirm button — only shown while editing -->
        <div v-if="zoomLevel >= 1 && state.textEditIsActive" class="flex flex-row items-center justify-center p-1 ml-auto">
          <div
            class="h-8 flex justify-center bg-black/20 items-center cursor-pointer rounded-full px-2"
            tabindex="0"
            :title="t('shift-enter-confirm')"
            @pointerdown.prevent.stop="toggleEditor()"
          >
            <!--input
              type="image"
              :src="CircleCheck"
              class="w-6 h-6 invert"
              :class="{ 'opacity-70 invert-0': note.entries[selectedBoardId].highlightColor.includes('bg-white') }"
              :alt="t('aria-edit-note')"
            /-->
            <span class="text-xs">{{ t('done') }}</span>
          </div>
        </div>
      </div>

      <!-- Content -->
      <div class="flex flex-col pt-2 px-2">
        <div v-if="!state.editHistoryIsVisible">
          <textarea
            v-if="actionAllowed && state.textEditIsActive"
            ref="newText"
            v-model.lazy="state.noteText"
            class="w-full rounded px-2 py-1 text-left font-medium text-lg mt-1 break-words textarea"
            :class="props.note.entries[selectedBoardId].highlightColor.includes('bg-white') ? 'bg-white' : 'bg-black'"
            spellcheck="false"
            autoselect
            @pointerdown.stop
            @pointermove.stop
            @keydown.shift.enter.prevent.stop="toggleEditor()"
            @input="autoResize"
          ></textarea>
          <!-- @blur="validateNewText()" -->
          <p
            v-if="actionAllowed && state.textEditIsActive"
            :class="props.note.entries[selectedBoardId].highlightColor.includes('bg-white') ? 'text-black' : 'text-gray-50'"
            class="w-full text-right text-[10px] opacity-60 pr-1 -mt-1"
          >
            {{ t('shift-enter-confirm') }}
          </p>
          <p
            v-if="actionAllowed && !state.textEditIsActive && state.noteText.length === 0"
            ref="textarea"
            class="w-full bg-transparent text-left font-medium text-lg mt-1 break-words pb-1 whitespace-pre-wrap"
          >
            {{ t('missingtext') }}
          </p>
          <p
            v-if="actionAllowed && !state.textEditIsActive && extractTags(state.noteText).length === 0"
            ref="textarea"
            class="w-full bg-transparent text-left font-medium text-lg mt-1 break-words pb-1 whitespace-pre-wrap"
          >
            {{ state.noteText }}
          </p>
          <p
            v-else-if="actionAllowed && !state.textEditIsActive"
            ref="textarea"
            class="w-full bg-transparent text-left font-medium text-lg mt-1 break-words pb-1 whitespace-pre-wrap"
          >
            {{ extractTags(state.noteText) }}
          </p>
          <p v-if="!actionAllowed" class="w-full bg-transparent text-left font-medium text-md mt-1 break-words whitespace-pre-wrap">
            {{ state.noteText }}
          </p>
        </div>
        <div v-else class="w-full fadein text-left flex flex-col max-h-36 overflow-y-scroll overflow-x-hidden" @pointerdown.prevent @wheel.stop>
          <span
            v-for="(edit, index) in edits"
            :key="index"
            :class="getBackgorundHighlight()"
            class="rounded text-left font-thin text-base my-1 break-words flex"
          >
            <span
              class="w-1 mr-1 rounded-xl block"
              :style="`background-color: ${color.actions.selectColor(edit.creator.username).background};`"
            ></span>
            <p class="edit-text-item p-2 bg-black/50">{{ edit.text }}</p>
          </span>
        </div>
        <div v-if="state.editHistoryIsVisible" class="flex flex-row">
          <p
            v-for="(name, index) in userNamesFromEdits"
            :key="index"
            class="text-left m-1"
            :style="{
              color: `${color.actions.selectColor(name).text}`,
              backgroundColor: `${color.actions.selectColor(name).background}`,
            }"
          >
            {{ name }}
          </p>
        </div>
        <div v-if="zoomLevel >= 1 && (props.note.tags.length || state.noteUrls.length)" class="flex mt-2 flex-wrap mb-2">
          <div
            v-for="(tag, index) in props.note.tags"
            :key="index"
            class="text-sm soft-btn-nh rounded-2xl px-2 py-1 mr-2 mt-2"
            :style="getKeywordStyle(props.note.entries[selectedBoardId].highlightColor)"
          >
            {{ tag }}
          </div>
          <div
            v-for="(url, index) in state.noteUrls"
            :key="index"
            class="text-sm text-white bg-slate-middle rounded-2xl py-1 mr-2 mt-2 cursor-pointer flex items-center px-2"
          >
            <input
              type="image"
              class="invert w-4 mr-1.5"
              :src="Link"
              :alt="t('aria-status-link')"
              @pointerdown.prevent.stop="invokeNavigationConfirmation(index)"
            />
            <p>{{ common.actions.cleanUrlString(url) }}</p>
          </div>
        </div>
      </div>

      <!-- Image -->
      <template v-if="props.note.attachment && props.note.attachment.type !== AttachmentType.none">
        <div class="rounded-t-lg max-h-60 self-center w-full">
          <div class="image-info-icon absolute w-5 h-5 bg-blue-middle rounded-full cursor-pointer flex justify-center align-center">
            <input
              type="image"
              v-if="!state.imageDetailsAreVisible"
              class="invert"
              :src="Info"
              :alt="t('aria-note-info')"
              @pointerdown.stop="toggleImageDetails()"
            />
            <input
              type="image"
              v-else
              class="invert w-4 h-4 rotate-45 mt-px"
              :src="Plus"
              :alt="t('aria-close')"
              @pointerdown.stop="toggleImageDetails()"
            />
          </div>
          <img
            v-if="!state.imageDetailsAreVisible"
            class="pointer-events-none rounded-t-lg object-cover max-h-60 w-full"
            :src="previewImage"
            @error="onImageLoadError()"
            :alt="t('aria-note-preview-image')"
          />
          <div v-else-if="state.imageDetailsAreVisible" class="rounded-t-lg p-4 text-white">
            <p class="font-bold" :class="state.textClass">{{ props.note.attachment.title || t('notitle') }}</p>
            <p class="italic" :class="state.textClass">{{ props.note.attachment.description || t('nodesc') }}</p>
          </div>
          <div v-else class="rounded-t-lg p-4 text-white">
            <p class="font-bold" :class="state.textClass">{{ t('nopreview') }}</p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { onMounted, onUnmounted, reactive, ref, inject, watch, computed, nextTick, toRefs, Ref, Teleport } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { Attachment, DialogMessageType, Note, Parcel } from '@/common/classes'
  import { ParcelType, AttachmentType, baseUrl, ScreensizeType } from '@/constants'

  import { useNoteStore } from '@/composables/noteStore'
  import { useUserStore } from '@/composables/userStore'
  import useWallStore from '@/composables/wallStore'

  import useCommonStore from '@/composables/common'
  import useFilterStore from '@/composables/filter'
  import useParcelStore from '@/composables/parcel'
  import useDialogStore from '@/composables/dialog'
  import useColorStore from '@/composables/colors'
  import useNoteSelection from '@/composables/noteSelection'

  import Notice from '../dialog/Notice.vue'
  import NoteMenu from './NoteMenu.vue'

  import CircleCheck from '@/assets/icons/fontawesome/circle-check-solid.svg'
  import History from '@/assets/icons/fontawesome/rectangle-history-regular.svg'
  import Menu from '@/assets/icons/fontawesome/ellipsis-solid.svg'
  import Pin from '@/assets/icons/fontawesome/thumbtack-sharp-solid.svg'
  import Link from '@/assets/icons/fontawesome/link-simple-regular.svg'
  import Edit from '@/assets/icons/fontawesome/pen-solid.svg'
  import BuildOn from '@/assets/icons/fontawesome/message-pen-light.svg'
  import Info from '@/assets/icons/fontawesome/circle-info-regular.svg'
  import Plus from '@/assets/icons/fontawesome/plus-solid.svg'
  import BrokenImage from '@/assets/icons/fontawesome/image-slash-solid.svg'

  const common = useCommonStore()
  const dialogStore = useDialogStore()
  const color = useColorStore()
  const noteStore = useNoteStore()
  const userStore = useUserStore()
  const filter = useFilterStore()
  const wallStore = useWallStore()
  const parcelStore = useParcelStore()

  const messages = {
    no: {
      creatorfail: 'Det oppstod en feil med å laste inn brukerdetaljene. Vennligst last siden på nytt og prøv igjen. ',
      dragnotpossible: 'Du må forstørre tavla for å flytte bidragene.',
      missingtext: 'Dette bidraget mangler tekst. Skriv noe...',
      contribution: 'bidrag',
      selected: 'valgt',
    },
    en: {
      creatorfail: 'Failed to fetch user details. Please reload the page and try again',
      dragnotpossible: 'Please zoom in to be able to drag the contributions',
      missingtext: 'This contribution lacks text. Write something...',
      contribution: 'contribution',
      selected: 'selected',
    },
  }

  const { t } = useI18n({ messages })

  const props = defineProps({
    note: { type: Note, required: true },
    zoomLevel: { type: Number, required: true },
    boardId: { type: String, required: true },
  })

  const openMenuNoteId = inject<Ref<string | null>>('openMenuNoteId')

  const noteSelection = useNoteSelection()
  const isSelected = computed(() => noteSelection.getters.isSelected(props.note._id))

  // The note is a keyboard tab stop (see useBoardKeyboard.ts), so give it a name that identifies it on its own
  const accessibleLabel = computed(() => {
    const text = props.note.text.length > 80 ? `${props.note.text.slice(0, 80)}…` : props.note.text
    const label = `${props.note.status.createdBy.username}: ${text}`
    return isSelected.value ? `${label}, ${t('selected')}` : label
  })

  const theNote = ref<HTMLInputElement | null>(null)
  const newText = ref<HTMLInputElement | null>(null)
  const textarea = ref<HTMLInputElement | null>(null)
  const noteOptionsWrapper = ref<HTMLInputElement | null>(null)
  const menuTriggerBtn = ref<HTMLElement | null>(null)
  const menuPosition = reactive({ bottom: 0, right: 0 })
  let imageRescrapeAttempted = false

  const { zoomLevel } = toRefs(props)

  const emit = defineEmits<{
    (e: 'invokeNavigationConfirmation', url: string): void
    (e: 'deleteNote', note: Note): void
    (e: 'setClickedNote', note: Note): void
    (e: 'showImagePopup', note: Note): void
    (e: 'liveTextLength', noteId: string, length: number): void // The parent determines the width of the Note. This helps the parent to adjust width based on note text content
  }>()

  const state = reactive({
    noteUrls: [] as string[],
    actualUrls: [] as string[],
    noteText: '',
    noteClass: 'bg-slate-bright border-white-dark text-slate-white',
    textClass: 'text-slate-bright',
    invalidUrl: false,
    menuIsVisible: false,
    dragInitiated: false,
    textEditIsActive: false,
    editHistoryIsVisible: false,
    imageErrorEncountered: false,
    imageDetailsAreVisible: false,
  })

  function handleOutsidePointerDown(event: PointerEvent) {
    if (!state.menuIsVisible) return
    const target = event.target as Node
    if (menuTriggerBtn.value && !menuTriggerBtn.value.contains(target)) {
      const menuEl = document.querySelector('.note-floating-menu')
      if (!menuEl || !menuEl.contains(target)) closeMenu()
    }
  }

  onMounted(async () => {
    state.noteText = props.note.text
    parseTextForUrl()
    document.addEventListener('pointerdown', handleOutsidePointerDown, true)
  })

  onUnmounted(() => {
    document.removeEventListener('pointerdown', handleOutsidePointerDown, true)
  })

  watch(
    () => openMenuNoteId?.value,
    (newId) => {
      if (newId !== props.note._id && state.menuIsVisible) {
        state.menuIsVisible = false
      }
    },
  )

  watch(
    () => props.zoomLevel,
    () => {
      if (state.menuIsVisible) closeMenu()
    },
  )

  //Must watch props.note.text for participants to detect note text changes.
  watch(
    () => props.note.text,
    (newText) => {
      state.noteText = newText
    },
  )

  const selectedBoardId = filter.getters.selectedBoardId

  const actionAllowed = computed(() => {
    const wall = wallStore.getters.selectedWall.value
    const user = userStore.getters.user.value
    if (user && wall) {
      return !wall.status.readOnly || user._id === wall.users.owner.user_id
    } else return false
  })

  const edits = computed(() => {
    return [props.note.origins[0], ...props.note.edits]
  })

  const previewImage = computed(() => {
    let src = BrokenImage
    if (props.note.attachment && props.note.attachment.url) {
      const url = props.note.attachment.url
      if (props.note.attachment.type === AttachmentType.image) src = url
      else if (props.note.attachment.type === AttachmentType.external) src = `${baseUrl}/api/file?url=${encodeURIComponent(url)}`
    }
    return src
  })

  async function onImageLoadError() {
    if (!imageRescrapeAttempted) {
      imageRescrapeAttempted = true
      console.log(`Bad image encountered: ${previewImage.value}. Scraping again..`)
      validateNewText(true)
    }
  }

  const userNamesFromEdits = computed(() => {
    let users = [] as string[]
    for (let edit of props.note.edits) {
      if (!users.includes(edit.creator.username) && edit.creator.username !== props.note.status.createdBy.username) users.push(edit.creator.username)
    }
    return users
  })

  const noteMetaStyles = computed(() => {
    return props.note.status.createdBy.username.length > 18 ? 'mb-3' : 'mb-1'
  })

  function showEditHistory() {
    state.textEditIsActive = false
    state.editHistoryIsVisible = !state.editHistoryIsVisible
  }

  function getKeywordStyle(color: string) {
    if (color.includes('bg-white')) return 'border-color: var(--bg-slate-middle)'
  }

  function shortenedUserName(name: string) {
    return name.length > 18 ? name.substring(0, 16).concat('..') : name
  }

  function extractTags(text: string) {
    let newText = text
    for (const url of state.actualUrls) newText = newText.replace(url, '')
    return newText
  }

  function getBackgorundHighlight() {
    const color = props.note.entries[props.boardId].highlightColor
    if (color.includes('bg-blue')) return 'text-slate-white bg-blue-dark'
    else if (color.includes('bg-white')) return 'text-black bg-gray-white'
    else return 'text-slate-white bg-slate-bright'
  }

  function toggleMenu() {
    state.menuIsVisible = !state.menuIsVisible
    if (state.menuIsVisible) {
      state.textEditIsActive = false
      if (openMenuNoteId) openMenuNoteId.value = props.note._id
      if (menuTriggerBtn.value) {
        const rect = menuTriggerBtn.value.getBoundingClientRect()
        menuPosition.right = window.innerWidth - rect.right
        menuPosition.bottom = window.innerHeight - rect.top + 4
      }
    } else {
      if (openMenuNoteId) openMenuNoteId.value = null
    }
    emit('setClickedNote', props.note)
  }

  function autoResize(event: Event) {
    const el = event.target as HTMLTextAreaElement
    el.style.height = 'auto'
    el.style.height = el.scrollHeight + 'px'
    emit('liveTextLength', props.note._id, el.value.length)
    nextTick(() => {
      el.style.height = 'auto'
      el.style.height = el.scrollHeight + 'px'
    })
  }

  function toggleEditor() {
    state.textEditIsActive = !state.textEditIsActive
    if (state.textEditIsActive) {
      state.menuIsVisible = false
      nextTick(() => {
        if (newText.value) {
          newText.value.focus()
          newText.value.style.height = 'auto'
          newText.value.style.height = newText.value.scrollHeight + 'px'
        }
      })
    } else validateNewText()
  }

  function invokeNavigationConfirmation(index: number) {
    emit('invokeNavigationConfirmation', state.actualUrls[index])
  }

  function parseTextForUrl() {
    let urls = common.actions.getActualUrlsFromText(state.noteText)
    state.noteUrls = urls
    state.actualUrls = urls
  }

  async function scrapeUrl() {
    parseTextForUrl()
    if (state.noteUrls.length > 0 && props.note.attachment && props.note.attachment.type !== AttachmentType.image) {
      const attachment = await common.actions.constructAttachmentFromScrape(state.noteUrls[0])
      if (attachment) {
        state.noteUrls = common.actions.sanitizeUrl(state.noteUrls)
        state.invalidUrl = false
        return attachment
      } else {
        state.noteUrls = []
        state.actualUrls = []
        state.invalidUrl = true
      }
    }
  }

  // 'forced' will potentially perform a scrape even if the note text has not changed
  async function validateNewText(forced = false) {
    const wall = wallStore.getters.selectedWall.value
    // NOTE: the way we trigger this operation might change
    if (newText.value && newText.value.value) state.noteText = newText.value.value
    if ((props.note.text !== state.noteText || forced) && wall) {
      let attachment = props.note.attachment || ({} as Attachment)
      if (props.note.attachment?.type !== AttachmentType.image) {
        const newAttachment = await scrapeUrl()
        if (newAttachment) attachment = newAttachment
        else {
          // The URL was removed
          attachment = {
            type: AttachmentType.none,
            title: '',
            description: '',
            url: '',
          }
        }
      }

      let updatedNote = new Note(props.note)
      if (attachment) updatedNote.attachment = attachment as Attachment
      let creator = userStore.getters.getUserAsCreator.value
      if (!creator) {
        const res = await userStore.actions.getUserDetails()
        if (res.success) creator = userStore.getters.getUserAsCreator.value
        else return dialogStore.actions.pushMessage('', DialogMessageType.Error, t('creatorfail'))
      } else {
        updatedNote.addEdit(state.noteText, creator)
        const res = await noteStore.actions.updateNote(updatedNote, wall._id, ParcelType.MessageEdited)
        if (res.success) {
          const parcel = new Parcel({ parcelType: ParcelType.MessageEdited, body: updatedNote }, userStore.getters.user.value, wall)
          await parcelStore.actions.sendParcel(wall._id, parcel)
          blurInput()
        }
      }
    } else blurInput()
  }

  function blurInput() {
    if (newText.value) {
      newText.value.blur()
      state.textEditIsActive = false
    }
  }

  function deleteNote() {
    emit('deleteNote', props.note)
  }

  function closeMenu() {
    state.menuIsVisible = false
    if (openMenuNoteId) openMenuNoteId.value = null
  }

  function toggleImageDetails() {
    state.imageDetailsAreVisible = !state.imageDetailsAreVisible
  }

  function imageLoadError(event: Event) {
    state.imageErrorEncountered = true
    console.error(event)
  }

  function imageLoad(event: Event) {
    state.imageErrorEncountered = false
  }

  function getEditIconByPermission() {
    return userIsCreator.value ? Edit : BuildOn
  }

  const userIsCreator = computed(() => {
    if (userStore.getters.user.value) {
      return props.note.status.createdBy.username === userStore.getters.user.value.profile.username
    } else return false
  })
</script>

<style scoped>
  .textarea {
    resize: none;
    overflow: hidden;
  }
  .edit-text-item {
    width: 99%;
  }
  .image-info-icon {
    left: 90%;
    top: 3%;
  }
</style>
